import React, { useState, useEffect, useRef } from 'react';

const topics = {
  fruitToy: {
    label: '🍎 Fruit vs Toy',
    left: '🍎 Fruit Box',
    right: '🧸 Toy Box',
    items: [
      { emoji: '🍎', type: 'fruit', name: 'Apple' },
      { emoji: '🍌', type: 'fruit', name: 'Banana' },
      { emoji: '🧸', type: 'toy', name: 'Teddy Bear' },
      { emoji: '🎲', type: 'toy', name: 'Dice' },
      { emoji: '🍇', type: 'fruit', name: 'Grapes' },
      { emoji: '🚗', type: 'toy', name: 'Car' },
    ],
  },
  petWild: {
    label: '🐶 Pet vs Wild Animal',
    left: '🐶 Pet',
    right: '🦁 Wild Animal',
    items: [
      { emoji: '🐶', type: 'pet', name: 'Dog' },
      { emoji: '🐱', type: 'pet', name: 'Cat' },
      { emoji: '🦁', type: 'wild', name: 'Lion' },
      { emoji: '🐯', type: 'wild', name: 'Tiger' },
      { emoji: '🐹', type: 'pet', name: 'Hamster' },
      { emoji: '🦓', type: 'wild', name: 'Zebra' },
    ],
  },
  clothesFood: {
    label: '👕 Clothes vs Food',
    left: '👕 Clothes',
    right: '🍕 Food',
    items: [
      { emoji: '👗', type: 'clothes', name: 'Dress' },
      { emoji: '👕', type: 'clothes', name: 'Shirt' },
      { emoji: '🍕', type: 'food', name: 'Pizza' },
      { emoji: '🍔', type: 'food', name: 'Burger' },
      { emoji: '🧦', type: 'clothes', name: 'Socks' },
      { emoji: '🍎', type: 'food', name: 'Apple' },
    ],
  },
  vehicleFurniture: {
    label: '🚗 Vehicle vs Furniture',
    left: '🚗 Vehicle',
    right: '🪑 Furniture',
    items: [
      { emoji: '🚗', type: 'vehicle', name: 'Car' },
      { emoji: '🛵', type: 'vehicle', name: 'Scooter' },
      { emoji: '🚲', type: 'vehicle', name: 'Bicycle' },
      { emoji: '🪑', type: 'furniture', name: 'Chair' },
      { emoji: '🛏️', type: 'furniture', name: 'Bed' },
      { emoji: '🛋️', type: 'furniture', name: 'Sofa' },
    ],
  },
  weatherSeason: {
    label: '☀️ Weather vs Season',
    left: '☀️ Weather',
    right: '🍂 Season',
    items: [
      { emoji: '🌧️', type: 'weather', name: 'Rain' },
      { emoji: '🌩️', type: 'weather', name: 'Storm' },
      { emoji: '❄️', type: 'season', name: 'Winter' },
      { emoji: '🌻', type: 'season', name: 'Spring' },
      { emoji: '☀️', type: 'weather', name: 'Sun' },
      { emoji: '🍁', type: 'season', name: 'Autumn' },
    ],
  },
};

const styles = {
  body: {
    fontFamily: "'Comic Sans MS', cursive",
    background: 'linear-gradient(to right, #d1c4e9, #b3e5fc)',
    textAlign: 'center',
    padding: 20,
    minHeight: '100vh',
  },
  h1: {
    fontSize: 32,
    color: '#4a148c',
    marginBottom: 10,
  },
  selector: {
    margin: 10,
    fontSize: 20,
    padding: 8,
    borderRadius: 10,
  },
  mainArea: {
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: 40,
  },
  dragArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 20,
    maxWidth: 300,
  },
  dragItems: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 15,
  },
  item: {
    width: 90,
    height: 90,
    fontSize: 50,
    cursor: 'grab',
    border: '4px solid #ffcc80',
    borderRadius: 20,
    backgroundColor: '#fff3e0',
    boxShadow: '2px 4px 6px rgba(0,0,0,0.2)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.3s',
    padding: 5,
    userSelect: 'none',
  },
  itemLabel: {
    fontSize: 14,
    marginTop: 4,
    color: '#333',
  },
  dropZones: {
    display: 'flex',
    flexDirection: 'column',
    gap: 40,
  },
  dropBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  dropZone: {
    width: 240,
    height: 240,
    border: '4px dashed #8c9eff',
    borderRadius: 24,
    backgroundColor: '#e8eaf6',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    padding: 10,
    gap: 10,
    overflowY: 'auto',
  },
  dropLabel: {
    marginTop: 8,
    fontWeight: 'bold',
    color: '#1a237e',
    fontSize: 20,
  },
  robotResult: {
    fontSize: 22,
    marginTop: 20,
    color: '#263238',
    whiteSpace: 'pre-wrap',
  },
  button: {
    fontSize: 22,
    padding: '12px 25px',
    backgroundColor: '#7c4dff',
    color: 'white',
    border: 'none',
    borderRadius: 10,
    cursor: 'pointer',
    margin: '20px 10px',
    boxShadow: '2px 2px 8px rgba(0,0,0,0.3)',
  },
};

function shuffle(arr) {
  return arr.slice().sort(() => 0.5 - Math.random());
}

function SupervisedLearningClassifier() {
  const [selectedTopic, setSelectedTopic] = useState('fruitToy');
  const [dragItems, setDragItems] = useState([]);
  const [leftItems, setLeftItems] = useState([]);
  const [rightItems, setRightItems] = useState([]);
  const [resultText, setResultText] = useState('');
  const [currentRound, setCurrentRound] = useState(0);
  const maxRounds = 20;

  // Load items on topic change or round change
  useEffect(() => {
    const topic = topics[selectedTopic];
    const shuffled = shuffle(topic.items).slice(0, 6);
    setDragItems(shuffled);
    setLeftItems([]);
    setRightItems([]);
    setResultText('');
  }, [selectedTopic, currentRound]);

  // Drag and Drop handlers
  const onDragStart = (e, item) => {
    e.dataTransfer.setData('type', item.type);
    e.dataTransfer.setData('emoji', item.emoji);
    e.dataTransfer.setData('name', item.name);
  };

  const onDragOver = e => e.preventDefault();

  const onDrop = (e, box) => {
    e.preventDefault();
    const type = e.dataTransfer.getData('type');
    const emoji = e.dataTransfer.getData('emoji');
    const name = e.dataTransfer.getData('name');
    const newItem = { type, emoji, name };

    if (box === 'left') {
      setLeftItems(prev => [...prev, newItem]);
    } else {
      setRightItems(prev => [...prev, newItem]);
    }
    // Remove dropped item from dragItems
    setDragItems(prev => prev.filter(i => !(i.name === name && i.emoji === emoji)));
  };

  // Robot testing logic
  const testRobot = () => {
    const topic = topics[selectedTopic];
    const testItems = shuffle(topic.items).slice(0, 4);
    let output = '';
    testItems.forEach(item => {
      // 50% chance robot guesses correctly
      const guessCorrect = Math.random() < 0.5;
      // If incorrect, pick the other category type
      const guessType = guessCorrect
        ? item.type
        : topic.items[0].type === item.type
          ? topic.items.find(i => i.type !== item.type).type
          : topic.items[0].type;

      if (item.type === guessType) {
        output += `🤖 Robot guessed ${item.emoji} is a ${guessType}! ✅\n`;
      } else {
        output += `🤖 Robot guessed ${item.emoji} is a ${guessType} ❌\n`;
        output += `👧 You corrected it! Robot says: "Thanks for teaching me! 💡"\n`;
      }
    });
    setResultText(output);
  };

  const nextRound = () => {
    if (currentRound < maxRounds - 1) {
      setCurrentRound(currentRound + 1);
      setResultText('');
    } else {
      setResultText('🎉 You finished all 20 training sessions! Great job!');
    }
  };

  return (
    <div style={styles.body}>
      <h1 style={styles.h1}>🤖 AI Sorting: Supervised Learning Fun!</h1>
      <select
        style={styles.selector}
        value={selectedTopic}
        onChange={e => {
          setSelectedTopic(e.target.value);
          setCurrentRound(0);
          setResultText('');
        }}
      >
        {Object.entries(topics).map(([key, topic]) => (
          <option key={key} value={key}>
            {topic.label}
          </option>
        ))}
      </select>

      <div style={styles.mainArea}>
        <div style={styles.dragArea}>
          <div><strong>Items to Sort:</strong></div>
          <div style={styles.dragItems}>
            {dragItems.map((item, idx) => (
              <div
                key={idx}
                style={styles.item}
                draggable
                onDragStart={e => onDragStart(e, item)}
                title={item.name}
                onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.2)')}
                onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
              >
                <div>{item.emoji}</div>
                <div style={styles.itemLabel}>{item.name}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={styles.dropZones}>
          <div style={styles.dropBox}>
            <div
              style={styles.dropZone}
              onDragOver={onDragOver}
              onDrop={e => onDrop(e, 'left')}
              aria-label="Left drop zone"
              role="list"
            >
              {leftItems.map((item, idx) => (
                <div key={idx} style={styles.item} title={item.name}>
                  <div>{item.emoji}</div>
                  <div style={styles.itemLabel}>{item.name}</div>
                </div>
              ))}
            </div>
            <div style={styles.dropLabel}>{topics[selectedTopic].left}</div>
          </div>

          <div style={styles.dropBox}>
            <div
              style={styles.dropZone}
              onDragOver={onDragOver}
              onDrop={e => onDrop(e, 'right')}
              aria-label="Right drop zone"
              role="list"
            >
              {rightItems.map((item, idx) => (
                <div key={idx} style={styles.item} title={item.name}>
                  <div>{item.emoji}</div>
                  <div style={styles.itemLabel}>{item.name}</div>
                </div>
              ))}
            </div>
            <div style={styles.dropLabel}>{topics[selectedTopic].right}</div>
          </div>
        </div>
      </div>

      <button style={styles.button} onClick={testRobot} type="button">
        Test the Robot
      </button>
      <button style={styles.button} onClick={nextRound} type="button">
        Next
      </button>

      <div style={styles.robotResult}>{resultText}</div>
    </div>
  );
}

export default SupervisedLearningClassifier;
